import * as S from "./styles.ts";
import { ColorKey } from "@template/theme";
import {
  CtaLink,
  DynamicIcon,
  H6,
  IconHolder,
  Image,
  P,
} from "@template/components";
import UniversalMarkdown from "@template/blocks/universal-markdown.tsx";
import { ImageProps } from "@template/components/Image";

export interface IInfoBlockProps {
  type?: "block" | "card";
  iconName?: string;
  color?: ColorKey;
  alignCenter?: boolean;
  heading?: string;
  subHeading?: string;
  cta?: {
    text: string;
    link: string;
    withIcon?: boolean;
    isDownloadable?: boolean;
    title?: string;
    target?: "_blank";
  };
  small?: boolean;
  filled?: boolean;
  image?: ImageProps;
  largeIcon?: boolean;
  boldSubHeading?: boolean;
}

const getSizeKey = (type: string, small: boolean) => {
  if (type === "card") {
    return small ? S.SizeType.SmallCard : S.SizeType.Card;
  } else if (type === "block") {
    return small ? S.SizeType.SmallBlock : S.SizeType.Block;
  }
};

const InfoBlock = ({
  iconName,
  color = "default",
  heading,
  subHeading,
  cta,
  small = false,
  type = "block",
  alignCenter = false,
  image,
  largeIcon,
  boldSubHeading = false,
}: IInfoBlockProps) => {
  const size = getSizeKey(type, small) || ("Block" as S.SizeType);
  const card = type === "card";

  const Icon = () => {
    return (
      <DynamicIcon
        name={iconName}
        height="100%"
        style={{
          width: "fit-content",
          maxWidth: "100%",
          maxHeight: "100%",
          ...(largeIcon
            ? {
                height: 36,
              }
            : {}),
        }}
      />
    );
  };

  return (
    <S.InfoBlockContainer
      $type={size}
      $alignCenter={alignCenter}
    >
      {card ? (
        image ? (
          <Image
            {...image}
            style={{
              width: "64px",
              height: "64px",
              objectFit: "contain",
            }}
          />
        ) : (
          <Icon />
        )
      ) : (
        <IconHolder color={color}>
          {image ? (
            <Image
              {...image}
              style={{
                objectFit: "contain",
                ...(largeIcon
                  ? {
                      height: 36,
                      width: 36,
                    }
                  : {
                      height: 30,
                      width: 30,
                    }),
              }}
            />
          ) : (
            <Icon />
          )}
        </IconHolder>
      )}
      <H6 as="h3">{heading}</H6>
      {typeof subHeading === "string" ? (
        subHeading.split("\n")?.map((paragraph, index) => (
          <P
            key={index}
            $bold={boldSubHeading}
          >
            {paragraph}{" "}
          </P>
        ))
      ) : (
        <P $bold={boldSubHeading}>{subHeading}</P>
      )}
      {cta?.text && (
        <CtaLink
          linkText={cta.text}
          href={cta.link}
          color={color}
          withIcon={cta.withIcon}
          title={cta.title}
          target={cta.target}
          isDownloadable={cta.isDownloadable}
        />
      )}
    </S.InfoBlockContainer>
  );
};

export default InfoBlock;
